import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
    Alert,
    Badge,
    Button,
    Card,
    Empty,
    Input,
    Modal,
    Select,
    Segmented,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    LeftOutlined,
    ReloadOutlined,
    RightOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { getAppointmentsByOrganization, getMedicScheduleByUser, getOrganizationMedics, getProfile, postUserAppointment } from '../store/user/thunks';
import { getAllPersonalRecords } from '../store/personalrecord/thunks';
import styles from './AppointmentManagement.module.scss';

const { Text, Title } = Typography;

type AnyRecord = Record<string, any>;

interface MedicSchedule {
    id?: string;
    centerName?: string;
    weekDayName?: string;
    type?: string;
    startHour?: string;
    endHour?: string;
    minutes?: number;
}

interface ScheduleException {
    id?: string;
    exceptionType?: string;
    exceptionDate?: string;
    startTime?: string;
    endTime?: string;
    reasonName?: string;
    weekDayName?: string;
    observation?: string;
}

interface MedicScheduleResponse {
    userId?: string;
    medicName?: string;
    schedules: MedicSchedule[];
    exceptions: ScheduleException[];
}

interface AppointmentItem {
    id?: string;
    dni?: string;
    name?: string;
    userId?: string;
    userName?: string;
    date?: string;
    hour?: string;
    status?: string;
    type?: string;
    specialityName?: string;
    centerName?: string;
    email?: string;
    phone?: string;
    paymentStatus?: string;
    serviceType?: string;
}

interface AppointmentPatient {
    dni: string;
    name: string;
    birthdate?: string;
}

interface AppointmentManagementProps {
    getProfile?: any;
    getMedicScheduleByUser?: any;
    getOrganizationMedics?: any;
    getAppointmentsByOrganization?: any;
    getAllPersonalRecords?: any;
    postUserAppointment?: any;
    organizationId?: string;
    organizationName?: string;
}

interface Slot {
    id: string;
    minutes: number;
    schedule: MedicSchedule;
    blocked: boolean;
}

const weekDayNames = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const weekDayLabels = ['LU', 'MA', 'MI', 'JU', 'VI', 'SA', 'DO'];
const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const valueOf = (source: AnyRecord | null | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key];
    }
    return undefined;
};

const toMinutes = (value?: string) => {
    if (!value) return 0;
    const [hours, minutes] = value.split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
};

const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
    const remainingMinutes = (minutes % 60).toString().padStart(2, '0');
    return `${hours}:${remainingMinutes}`;
};

const getWeekDayIndex = (value?: string) => {
    if (!value) return -1;
    const normalized = value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return weekDayNames.findIndex(day => day.normalize('NFD').replace(/[\u0300-\u036f]/g, '') === normalized);
};

const normalizeSchedule = (source: AnyRecord | null | undefined): MedicScheduleResponse => {
    const schedules = valueOf(source, 'schedules', 'Schedules');
    const exceptions = valueOf(source, 'exceptions', 'Exceptions');

    return {
        userId: valueOf(source, 'userId', 'UserId'),
        medicName: valueOf(source, 'medicName', 'MedicName'),
        schedules: Array.isArray(schedules) ? schedules.map((item: AnyRecord) => ({
            id: valueOf(item, 'id', 'Id'),
            centerName: valueOf(item, 'centerName', 'CenterName'),
            weekDayName: valueOf(item, 'weekDayName', 'WeekDayName'),
            type: valueOf(item, 'type', 'Type'),
            startHour: valueOf(item, 'startHour', 'StartHour'),
            endHour: valueOf(item, 'endHour', 'EndHour'),
            minutes: Number(valueOf(item, 'minutes', 'Minutes') || 30),
        })) : [],
        exceptions: Array.isArray(exceptions) ? exceptions.map((item: AnyRecord) => ({
            id: valueOf(item, 'id', 'Id'),
            exceptionType: valueOf(item, 'exceptionType', 'ExceptionType'),
            exceptionDate: valueOf(item, 'exceptionDate', 'ExceptionDate'),
            startTime: valueOf(item, 'startTime', 'StartTime'),
            endTime: valueOf(item, 'endTime', 'EndTime'),
            reasonName: valueOf(item, 'reasonName', 'ReasonName'),
            weekDayName: valueOf(item, 'weekDayName', 'WeekDayName'),
            observation: valueOf(item, 'observation', 'Observation'),
        })) : [],
    };
};

const normalizeAppointments = (source: AnyRecord | AnyRecord[] | null | undefined): AppointmentItem[] => {
    const values = Array.isArray(source) ? source : valueOf(source, 'appointments', 'Appointments');
    return Array.isArray(values) ? values.map((item: AnyRecord) => ({
        id: valueOf(item, 'id', 'Id'),
        dni: valueOf(item, 'dni', 'Dni'),
        name: valueOf(item, 'name', 'Name'),
        userId: valueOf(item, 'userId', 'UserId'),
        userName: valueOf(item, 'userName', 'UserName'),
        date: valueOf(item, 'date', 'Date'),
        hour: valueOf(item, 'hour', 'Hour'),
        status: valueOf(item, 'status', 'Status'),
        type: valueOf(item, 'type', 'Type'),
        specialityName: valueOf(item, 'specialityName', 'SpecialityName'),
        centerName: valueOf(item, 'centerName', 'CenterName'),
        email: valueOf(item, 'email', 'Email'),
        phone: valueOf(item, 'phone', 'Phone'),
        paymentStatus: valueOf(item, 'paymentStatus', 'PaymentStatus'),
        serviceType: valueOf(item, 'serviceType', 'ServiceType'),
    })) : [];
};

const normalizePatients = (source: AnyRecord | AnyRecord[] | null | undefined): AppointmentPatient[] => {
    const values = Array.isArray(source) ? source : valueOf(source, 'personalRecords', 'PersonalRecords', 'items', 'Items', 'data', 'Data');

    return Array.isArray(values) ? values.map((item: AnyRecord) => ({
        dni: String(valueOf(item, 'dni', 'Dni') || ''),
        name: [
            valueOf(item, 'firstName', 'FirstName'),
            valueOf(item, 'secondName', 'SecondName'),
            valueOf(item, 'lastName', 'LastName'),
            valueOf(item, 'secondLastname', 'SecondLastname'),
        ].filter(Boolean).join(' ') || String(valueOf(item, 'name', 'Name') || 'Paciente sin nombre'),
        birthdate: valueOf(item, 'birthdate', 'Birthdate'),
    })).filter(patient => patient.dni) : [];
};

const appointmentDateKey = (value?: string) => {
    if (!value) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        const [day, month, year] = value.split('/');
        return `${year}-${month}-${day}`;
    }
    return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : value;
};

const appointmentHourKey = (value?: string) => (value || '').slice(0, 5);

const AppointmentManagement: React.FC<AppointmentManagementProps> = ({
    getProfile: loadProfile,
    getMedicScheduleByUser: loadMedicSchedule,
    getOrganizationMedics: loadOrganizationMedics,
    getAppointmentsByOrganization: loadOrganizationAppointments,
    getAllPersonalRecords: loadPatients,
    postUserAppointment: createAppointment,
    organizationId,
    organizationName,
}) => {
    const [schedule, setSchedule] = useState<MedicScheduleResponse | null>(null);
    const [appointments, setAppointments] = useState<AppointmentItem[]>([]);
    const [appointmentFilter, setAppointmentFilter] = useState<'ALL' | 'PENDING' | 'IN_CONSULTATION' | 'ATTENDED'>('ALL');
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs().startOf('month'));
    const [viewMode, setViewMode] = useState<'week' | 'month'>('month');
    const [agendaUserId, setAgendaUserId] = useState<string | null>(null);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [scheduleSlot, setScheduleSlot] = useState<Slot | null>(null);
    const [schedulePatients, setSchedulePatients] = useState<AppointmentPatient[]>([]);
    const [schedulePatientSearch, setSchedulePatientSearch] = useState('');
    const [scheduleSelectedPatient, setScheduleSelectedPatient] = useState<AppointmentPatient | null>(null);
    const [scheduleType, setScheduleType] = useState<'PRIMERA' | 'SUBSECUENTE'>('PRIMERA');
    const [schedulePatientsLoading, setSchedulePatientsLoading] = useState(false);
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setLoading(true);
            setError(null);

            try {
                const profile = await loadProfile?.();
                const profileUserId = valueOf(profile, 'id', 'Id')
                    || valueOf(valueOf(profile, 'user', 'User'), 'id', 'Id');

                if (!profileUserId && !organizationId) {
                    throw new Error('No se pudo identificar al usuario del médico.');
                }

                let userId = profileUserId;
                if (organizationId && loadOrganizationMedics) {
                    const medicsResponse = await loadOrganizationMedics(organizationId);
                    const medics = Array.isArray(medicsResponse) ? medicsResponse : [];
                    userId = valueOf(medics.find((item: AnyRecord) => String(valueOf(item, 'id', 'Id')) === String(profileUserId)), 'id', 'Id')
                        || valueOf(medics[0], 'id', 'Id');
                }

                if (!userId) {
                    throw new Error('La organización no tiene médicos con agenda disponible.');
                }

                const [response, appointmentsResponse] = await Promise.all([
                    loadMedicSchedule?.(String(userId), organizationId),
                    loadOrganizationAppointments?.(organizationId),
                ]);
                if (!response) {
                    throw new Error('No se encontró la agenda del médico.');
                }

                if (mounted) {
                    setAgendaUserId(String(userId));
                    setSchedule(normalizeSchedule(response));
                    setAppointments(normalizeAppointments(appointmentsResponse));
                }
            } catch (loadError) {
                if (mounted) {
                    setSchedule(null);
                    setAgendaUserId(null);
                    setAppointments([]);
                    setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar la agenda.');
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, [loadMedicSchedule, loadProfile, loadOrganizationMedics, loadOrganizationAppointments, organizationId, refreshKey]);

    const calendarDays = useMemo(() => {
        if (viewMode === 'week') {
            const monday = selectedDate.subtract((selectedDate.day() + 6) % 7, 'day');
            return Array.from({ length: 7 }, (_, index) => monday.add(index, 'day'));
        }

        const firstDayOfMonth = currentMonth.startOf('month');
        const mondayOffset = (firstDayOfMonth.day() + 6) % 7;
        const firstCalendarDay = firstDayOfMonth.subtract(mondayOffset, 'day');
        return Array.from({ length: 42 }, (_, index) => firstCalendarDay.add(index, 'day'));
    }, [currentMonth, selectedDate, viewMode]);

    const getSchedulesForDate = useCallback((date: Dayjs) => (schedule?.schedules || [])
        .filter(item => getWeekDayIndex(item.weekDayName) === date.day()), [schedule]);

    const getExceptionsForDate = useCallback((date: Dayjs) => (schedule?.exceptions || [])
        .filter((exception) => {
            const exceptionDate = exception.exceptionDate;
            if (exceptionDate && dayjs(exceptionDate).isValid()) {
                return dayjs(exceptionDate).isSame(date, 'day');
            }
            return getWeekDayIndex(exception.weekDayName) === date.day();
        }), [schedule]);

    const slots = useMemo<Slot[]>(() => {
        const exceptions = getExceptionsForDate(selectedDate);
        const result: Slot[] = [];

        getSchedulesForDate(selectedDate).forEach((item, scheduleIndex) => {
            const start = toMinutes(item.startHour);
            const end = toMinutes(item.endHour);
            const interval = Number(item.minutes) > 0 ? Number(item.minutes) : 30;

            for (let cursor = start; cursor < end; cursor += interval) {
                const blocked = exceptions.some((exception) => {
                    if (String(exception.exceptionType || '').toUpperCase() === 'COMPLETO') return true;
                    const exceptionStart = exception.startTime ? toMinutes(exception.startTime) : start;
                    const exceptionEnd = exception.endTime ? toMinutes(exception.endTime) : end;
                    return cursor < exceptionEnd && cursor + interval > exceptionStart;
                });

                result.push({
                    id: `${item.id || scheduleIndex}-${cursor}`,
                    minutes: cursor,
                    schedule: item,
                    blocked,
                });
            }
        });

        return result;
    }, [getExceptionsForDate, getSchedulesForDate, selectedDate]);

    const selectedDateKey = selectedDate.format('YYYY-MM-DD');
    const appointmentsForSelectedDate = appointments.filter(appointment => appointmentDateKey(appointment.date) === selectedDateKey);
    const appointmentStatusGroup = (status?: string) => {
        const normalized = String(status || '').toUpperCase();
        if (normalized.includes('CONSULT') || normalized.includes('SALA')) return 'IN_CONSULTATION';
        if (normalized.includes('ATEND') || normalized.includes('COMPLET')) return 'ATTENDED';
        return 'PENDING';
    };
    const appointmentStatusLabel = (status?: string) => {
        const group = appointmentStatusGroup(status);
        if (group === 'IN_CONSULTATION') return 'En consulta';
        if (group === 'ATTENDED') return 'Atendido';
        return 'Pendiente';
    };
    const filteredAppointments = appointmentsForSelectedDate.filter((appointment) => appointmentFilter === 'ALL' || appointmentStatusGroup(appointment.status) === appointmentFilter);
    const filteredSchedulePatients = useMemo(() => {
        const query = schedulePatientSearch.trim().toLowerCase();
        if (!query) return schedulePatients;
        return schedulePatients.filter(patient => `${patient.name} ${patient.dni}`.toLowerCase().includes(query));
    }, [schedulePatientSearch, schedulePatients]);
    const appointmentCounts = {
        all: appointmentsForSelectedDate.length,
        pending: appointmentsForSelectedDate.filter(item => appointmentStatusGroup(item.status) === 'PENDING').length,
        inConsultation: appointmentsForSelectedDate.filter(item => appointmentStatusGroup(item.status) === 'IN_CONSULTATION').length,
        attended: appointmentsForSelectedDate.filter(item => appointmentStatusGroup(item.status) === 'ATTENDED').length,
    };
    const getAppointmentForSlot = useCallback((slot: Slot) => appointmentsForSelectedDate.find((appointment) => appointmentHourKey(appointment.hour) === formatTime(slot.minutes)), [appointmentsForSelectedDate]);
    const availableSlots = slots.filter(slot => !slot.blocked && !getAppointmentForSlot(slot)).length;
    const selectedExceptions = getExceptionsForDate(selectedDate);
    const monthTitle = `${monthNames[currentMonth.month()]} ${currentMonth.year()}`;
    const selectedTitle = `${weekDayNames[selectedDate.day()].charAt(0).toUpperCase()}${weekDayNames[selectedDate.day()].slice(1)} ${selectedDate.date()} de ${monthNames[selectedDate.month()].toLowerCase()}`;
    const openScheduleModal = async (slot: Slot) => {
        if (slot.blocked || getAppointmentForSlot(slot)) return;

        setScheduleSlot(slot);
        setSchedulePatientSearch('');
        setScheduleSelectedPatient(null);
        setScheduleType('PRIMERA');
        setScheduleModalOpen(true);

        if (schedulePatients.length > 0 || !loadPatients) return;

        setSchedulePatientsLoading(true);
        try {
            const response = await loadPatients(organizationId);
            setSchedulePatients(normalizePatients(response));
        } finally {
            setSchedulePatientsLoading(false);
        }
    };
    const closeScheduleModal = () => {
        if (scheduleSubmitting) return;
        setScheduleModalOpen(false);
        setScheduleSlot(null);
        setScheduleSelectedPatient(null);
    };
    const submitSchedule = async () => {
        if (!scheduleSlot || !scheduleSelectedPatient || !agendaUserId || !createAppointment) return;

        setScheduleSubmitting(true);
        try {
            const response = await createAppointment({
                Dni: scheduleSelectedPatient.dni,
                UserId: agendaUserId,
                Date: selectedDate.format('YYYY-MM-DD'),
                Hour: formatTime(scheduleSlot.minutes),
                Canal: 'ADMIN',
                Type: scheduleType,
                OrganizationId: organizationId || null,
                OrganizationName: organizationName || null,
            });

            if (response === undefined) return;

            const responseRecord = response && typeof response === 'object' ? response as AnyRecord : null;
            const localAppointment: AppointmentItem = {
                id: String(valueOf(responseRecord, 'id', 'Id') || `${scheduleSelectedPatient.dni}-${selectedDate.format('YYYY-MM-DD')}-${formatTime(scheduleSlot.minutes)}`),
                dni: scheduleSelectedPatient.dni,
                name: scheduleSelectedPatient.name,
                userId: agendaUserId,
                userName: schedule?.medicName,
                date: selectedDate.format('DD/MM/YYYY'),
                hour: formatTime(scheduleSlot.minutes),
                status: 'AGENDADO',
                type: scheduleType,
                serviceType: 'CONSULTA',
                centerName: scheduleSlot.schedule.centerName,
            };

            setAppointments(current => [...current, localAppointment]);
            setScheduleModalOpen(false);
            setScheduleSlot(null);
            setScheduleSelectedPatient(null);
        } finally {
            setScheduleSubmitting(false);
        }
    };
    const changeMonth = (amount: number) => {
        const nextMonth = currentMonth.add(amount, 'month');
        setCurrentMonth(nextMonth);
        setSelectedDate(nextMonth.date(Math.min(selectedDate.date(), nextMonth.daysInMonth())));
    };

    if (loading) return <div className={styles.loading}><Spin /></div>;

    if (error) return <Alert type="warning" showIcon message="Agenda no disponible" description={error} action={<Button icon={<ReloadOutlined />} onClick={() => setRefreshKey(value => value + 1)}>Reintentar</Button>} />;

    return <div className={styles.page}>
        <div className={styles.agendaHeader}>
            <div>
                <Title level={3}>Agenda</Title>
                <Text type="secondary">{organizationName || 'Organización'} · {schedule?.medicName || 'Médico'} · Horarios configurados</Text>
            </div>
            <Button icon={<ReloadOutlined />} onClick={() => setRefreshKey(value => value + 1)}>Actualizar</Button>
        </div>

        <div className={styles.agendaToolbar}>
            <Space size={8}>
                <Button aria-label="Mes anterior" icon={<LeftOutlined />} onClick={() => changeMonth(-1)} />
                <Button className={styles.monthButton}>{monthTitle}</Button>
                <Button aria-label="Mes siguiente" icon={<RightOutlined />} onClick={() => changeMonth(1)} />
            </Space>
            <Segmented
                options={[{ label: 'Semana', value: 'week' }, { label: 'Mes', value: 'month' }]}
                value={viewMode}
                onChange={(value) => setViewMode(value as 'week' | 'month')}
            />
            <Space className={styles.legend} wrap>
                <Badge color="#2167dc" text="Disponible" />
                <Badge color="#ef4444" text="Bloqueado" />
            </Space>
        </div>

        <div className={styles.agendaLayout}>
            <Card className={styles.calendarCard}>
                <div className={styles.weekHeader}>
                    {weekDayLabels.map(label => <Text key={label} type="secondary">{label}</Text>)}
                </div>
                <div className={styles.calendarGrid}>
                    {calendarDays.map((date) => {
                        const dayAppointments = appointments.filter(appointment => appointmentDateKey(appointment.date) === date.format('YYYY-MM-DD'));
                        const dayExceptions = getExceptionsForDate(date);
                        const isSelected = date.isSame(selectedDate, 'day');
                        const isOutsideMonth = date.month() !== currentMonth.month();
                        const isToday = date.isSame(dayjs(), 'day');
                        return <button
                            type="button"
                            key={date.format('YYYY-MM-DD')}
                            className={`${styles.dayCell} ${isSelected ? styles.dayCellSelected : ''} ${isOutsideMonth ? styles.dayCellOutside : ''}`}
                            onClick={() => {
                                setSelectedDate(date);
                                if (date.month() !== currentMonth.month()) setCurrentMonth(date.startOf('month'));
                            }}
                        >
                            <span className={`${styles.dayNumber} ${isToday ? styles.dayNumberToday : ''}`}>{date.date()}</span>
                            {dayAppointments.length > 0 && <span className={styles.dayMeta}><Badge color="#2167dc" /> {dayAppointments.length} persona{dayAppointments.length === 1 ? '' : 's'}</span>}
                            {dayExceptions.length > 0 && <span className={styles.dayMeta}><Badge color="#ef4444" /> Excepción</span>}
                        </button>;
                    })}
                </div>
            </Card>

            <Card className={styles.dayCard}>
                <div className={styles.dayHeading}>
                    <div>
                        <Title level={4}>{selectedTitle}</Title>
                        <Text type="secondary">{schedule?.medicName || 'Agenda médica'}</Text>
                    </div>
                    <CalendarOutlined className={styles.dayIcon} />
                </div>
                <div className={styles.metrics}>
                    <div className={styles.metric}><strong>{appointmentsForSelectedDate.length}</strong><Text type="secondary">Citas</Text></div>
                    <div className={styles.metric}><strong>{availableSlots}</strong><Text type="secondary">Disponible</Text></div>
                </div>
                <div className={styles.appointmentPanel}>
                    <div className={styles.appointmentPanelHeader}>
                        <div>
                            <Title level={5}>Agenda de hoy</Title>
                            <Text type="secondary">{appointmentsForSelectedDate.length} pacientes registrados</Text>
                        </div>
                        <Segmented
                            size="small"
                            value={appointmentFilter}
                            onChange={(value) => setAppointmentFilter(value as typeof appointmentFilter)}
                            options={[
                                { label: `Todos ${appointmentCounts.all}`, value: 'ALL' },
                                { label: `Pendientes ${appointmentCounts.pending}`, value: 'PENDING' },
                                { label: `En consulta ${appointmentCounts.inConsultation}`, value: 'IN_CONSULTATION' },
                                { label: `Atendidos ${appointmentCounts.attended}`, value: 'ATTENDED' },
                            ]}
                        />
                    </div>
                    {filteredAppointments.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay citas para este filtro" /> : <div className={styles.appointmentList}>
                        {filteredAppointments.map((appointment) => <div key={appointment.id || `${appointment.dni}-${appointment.hour}`} className={styles.appointmentRow}>
                            <div className={styles.appointmentAvatar}>{(appointment.name || appointment.dni || 'P').replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ]/g, '').slice(0, 2).toUpperCase()}</div>
                            <div className={styles.appointmentMain}>
                                <Text strong>{appointment.name || 'Paciente sin nombre'}</Text>
                                <Text type="secondary">{appointment.specialityName || appointment.serviceType || 'Consulta'}{appointment.type ? ` · ${appointment.type}` : ''}</Text>
                            </div>
                            <div className={styles.appointmentMeta}>
                                <Tag color={appointmentStatusGroup(appointment.status) === 'ATTENDED' ? 'success' : appointmentStatusGroup(appointment.status) === 'IN_CONSULTATION' ? 'processing' : 'default'}>{appointmentStatusLabel(appointment.status)}</Tag>
                                <Text strong>{appointmentHourKey(appointment.hour)}</Text>
                            </div>
                        </div>)}
                    </div>}
                </div>
                {(selectedExceptions.length > 0) && <Alert
                    type="warning"
                    showIcon
                    message="Excepción configurada"
                    description={selectedExceptions.map(exception => exception.reasonName || exception.observation || exception.exceptionType).filter(Boolean).join(' · ')}
                    className={styles.exceptionAlert}
                />}
                {slots.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay horarios configurados para este día" /> : <div className={styles.slotList}>
                    {slots.map(slot => {
                        const appointment = getAppointmentForSlot(slot);
                        const canSchedule = !slot.blocked && !appointment;
                        return <div
                            key={slot.id}
                            className={`${styles.slotRow} ${slot.blocked ? styles.slotBlocked : ''} ${appointment ? styles.slotBooked : ''} ${canSchedule ? styles.slotAvailable : ''}`}
                            onClick={() => canSchedule && openScheduleModal(slot)}
                            onKeyDown={(event) => {
                                if (canSchedule && (event.key === 'Enter' || event.key === ' ')) {
                                    event.preventDefault();
                                    openScheduleModal(slot);
                                }
                            }}
                            role={canSchedule ? 'button' : undefined}
                            tabIndex={canSchedule ? 0 : undefined}
                        >
                            <Tag className={styles.timeTag}><ClockCircleOutlined /> {formatTime(slot.minutes)}</Tag>
                            <div className={styles.slotInfo}>
                                <Space wrap size={[6, 4]}>
                                    <Text strong>{slot.blocked ? 'Bloqueado' : appointment ? `Ocupado · ${appointment.name || appointment.dni || 'Paciente'}` : 'Disponible'}</Text>
                                    <Tag>{slot.schedule.type || 'NORMAL'}</Tag>
                                    {appointment?.status && <Tag color="processing">{appointmentStatusLabel(appointment.status)}</Tag>}
                                    {slot.schedule.centerName && <Tag icon={<EnvironmentOutlined />}>{slot.schedule.centerName}</Tag>}
                                </Space>
                                <Text type="secondary">Duración: {slot.schedule.minutes || 30} minutos</Text>
                            </div>
                        </div>;
                    })}
                </div>}
            </Card>
        </div>
        <Modal
            open={scheduleModalOpen}
            title="Agendar cita"
            onCancel={closeScheduleModal}
            destroyOnClose
            okText="Agendar cita"
            cancelText="Cancelar"
            confirmLoading={scheduleSubmitting}
            onOk={submitSchedule}
            okButtonProps={{ disabled: !scheduleSlot || !scheduleSelectedPatient || !agendaUserId }}
        >
            {scheduleSlot && <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message="Horario seleccionado"
                    description={`${selectedTitle} · ${formatTime(scheduleSlot.minutes)} · ${scheduleSlot.schedule.centerName || organizationName || 'Centro'}`}
                />
                <div>
                    <Text strong>Paciente</Text>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Buscar por nombre o cédula..."
                        value={schedulePatientSearch}
                        onChange={(event) => setSchedulePatientSearch(event.target.value)}
                    />
                </div>
                {schedulePatientsLoading ? <div className={styles.medicalLoading}><Spin /></div> : filteredSchedulePatients.length === 0 ? <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No hay pacientes registrados en esta organización"
                /> : <div className={styles.schedulePatientList}>
                    {filteredSchedulePatients.map(patient => <button
                        type="button"
                        key={patient.dni}
                        className={`${styles.schedulePatientOption} ${scheduleSelectedPatient?.dni === patient.dni ? styles.schedulePatientOptionSelected : ''}`}
                        onClick={() => setScheduleSelectedPatient(patient)}
                    >
                        <div className={styles.appointmentAvatar}>{patient.name.replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ]/g, '').slice(0, 2).toUpperCase()}</div>
                        <div className={styles.appointmentMain}>
                            <Text strong>{patient.name}</Text>
                            <Text type="secondary">C.I. {patient.dni}</Text>
                        </div>
                    </button>)}
                </div>}
                <div>
                    <Text strong>Tipo de cita</Text>
                    <Select
                        style={{ width: '100%' }}
                        value={scheduleType}
                        options={[
                            { label: 'Primera', value: 'PRIMERA' },
                            { label: 'Subsecuente', value: 'SUBSECUENTE' },
                        ]}
                        onChange={(value) => setScheduleType(value as 'PRIMERA' | 'SUBSECUENTE')}
                    />
                </div>
            </Space>}
        </Modal>
    </div>;
};

export default connect(null, {
    getProfile,
    getMedicScheduleByUser,
    getOrganizationMedics,
    getAppointmentsByOrganization,
    getAllPersonalRecords,
    postUserAppointment,
})(AppointmentManagement);
